import React from "react";
import { PlayerDetails } from "../../../types";
import { useUser } from "../auth/is_loggedin";
import { useTranslation } from "react-i18next";
import useKonami from "./konami";
import Supporter from "./support";
import { usePlayerSettings } from "../../../hooks/player-settings/usePlayerSettings";
import { usePlayerPersistence } from "../../../hooks/player-settings/usePlayerPersistence";
import { useMenuState } from "../../../hooks/useMenuState";
import { useMenuNavigation } from "../../../hooks/useMenuNavigation";
import { useUIPreferences } from "../../../hooks/useUIPreferences";
import AuthenticationSection from "./sections/AuthenticationSection";
import AvatarSelection from "./sections/AvatarSelection";
import ThemeCustomization from "./sections/ThemeCustomization";
import PageLayout from "../../common/page-layout";
import AvatarHeader from "../../common/avatar-header/avatar-header";
import SubheaderBase from "../../common/subheader/subheader-base";
import FormFooter from "../../common/form-footer/form-footer";
import ScrollableContent from "../../common/scrollable-content";
import FormCard from "../../common/form-card";
import { Input } from "../../ui/input";
import RadioInput from "./radioinput";
import { FontOptions } from "../../../types";

type Props = {
  player?: PlayerDetails;
  setupMode?: boolean;
};

function Name({ player, setupMode }: Props) {
  const { t } = useTranslation("main", { keyPrefix: "profile" });
  const user = useUser();
  const konami = useKonami();

  // Get player details and meta state from Redux
  const { playerDetails, loading, error } = useMenuState();
  const handleBack = useMenuNavigation();

  // Use Redux state or prop, fallback to localStorage if neither available
  const effectivePlayer = playerDetails || player;

  // Call all hooks unconditionally (Rules of Hooks)
  const { settings, updateProfile, updateAvatar } = usePlayerSettings(
    effectivePlayer,
    user,
    konami
  );

  const { theme, updateTheme } = useUIPreferences();

  const { saveSettings, isValid, validationError } =
    usePlayerPersistence(settings);

  // Auth loading is handled by usePlayerSettings - it validates avatars after auth resolves
  // No need for a separate loading state since the page renders with sensible defaults

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!isValid) {
      alert(validationError || "NO NAME?!");
      return;
    }
    saveSettings(settings, setupMode);
  };

  const handleColorScheme = (event: React.ChangeEvent<HTMLInputElement>) => {
    const val = Number.parseInt(event.target.value);
    // Update UI preferences (which handles both Redux and localStorage)
    updateTheme(val);
  };

  return (
    <PageLayout as="form" onSubmit={handleSubmit}>
      <AvatarHeader
        hideAvatar
        titles={[t("headers.default")]}
        avatarName={playerDetails?.name}
        avatarSpriteId={playerDetails?.meta.sprite_id}
      />

      <SubheaderBase subheaders={[t("subheaders.default")]} />

      <ScrollableContent>
        <div className="flex flex-col items-center space-y-4 my-2 px-4">
          {/* Auth Section - already has its own card styling */}
          <AuthenticationSection user={user} />

          {/* Name Input */}
          <FormCard title={"enter your name:"} description="">
            <Input
              variant="dark"
              inputWidth="full"
              onChange={(e) => updateProfile({ name: e.target.value })}
              id={"name-input"}
              value={settings.profile.name}
              autoComplete={"off"}
              minLength={3}
              maxLength={10}
              className="text-center"
              placeholder="your name here"
              required
            />
          </FormCard>

          {/* Avatar Selection */}
          <FormCard title="select your Avatar:" description="">
            <AvatarSelection
              spriteId={settings.avatar.spriteId}
              onChange={updateAvatar}
              user={user}
              konami={konami}
            />
          </FormCard>

          {/* 18+ Mature Content */}
          <FormCard
            title={"please verify your age:"}
            description="users under 18 cannot access adult themes"
          >
            <div className="flex flex-row gap-2 w-full justify-end">
              <RadioInput
                name={"player-mature"}
                id={"player-mature-no"}
                onChange={(e: any) =>
                  updateProfile({ mature: e.target.value === "true" })
                }
                checked={!settings.profile.mature}
                value={"false"}
                label={"I am under 18"}
                textSize="text-xl"
                className="flex-1"
              />
              <RadioInput
                name={"player-mature"}
                id={"player-mature-yes"}
                onChange={(e: any) =>
                  updateProfile({ mature: e.target.value === "true" })
                }
                checked={settings.profile.mature}
                value={"true"}
                label={"i am 18 or over"}
                textSize="text-xl"
                className="flex-1"
              />
            </div>
          </FormCard>

          {/* Font Selection */}
          <FormCard title={"choose a font:"} description="">
            <div className="flex flex-row gap-2 w-full justify-end">
              <RadioInput
                name={"player-accessibility"}
                id={"player-accessibility-no"}
                onChange={(e: any) =>
                  updateProfile({
                    accessibility: Number.parseInt(e.target.value),
                  })
                }
                checked={settings.profile.accessibility === FontOptions.DEFAULT}
                value={FontOptions.DEFAULT}
                label={"classic"}
                textSize="text-sm"
                className="flex-1"
              />
              <RadioInput
                name={"player-accessibility"}
                id={"player-accessibility-comic"}
                onChange={(e: any) =>
                  updateProfile({
                    accessibility: Number.parseInt(e.target.value),
                  })
                }
                checked={
                  settings.profile.accessibility === FontOptions.COMIC_SANS
                }
                value={FontOptions.COMIC_SANS}
                label={"silly"}
                fontFamily={"Comic Sans"}
                textSize="text-sm"
                className="flex-1"
              />
              <RadioInput
                name={"player-accessibility"}
                id={"player-accessibility-dyslexia"}
                onChange={(e: any) =>
                  updateProfile({
                    accessibility: Number.parseInt(e.target.value),
                  })
                }
                checked={
                  settings.profile.accessibility === FontOptions.DYSLEXIA
                }
                value={FontOptions.DYSLEXIA}
                label={"Open Dyslexic"}
                fontFamily={"Open-Dyslexic"}
                textSize="text-xs"
                className="flex-1"
              />
            </div>
          </FormCard>

          {/* Theme Selection */}
          <FormCard title={"choose a theme:"} description="">
            <ThemeCustomization
              colorScheme={theme}
              onChange={handleColorScheme}
              user={user}
            />
          </FormCard>
        </div>

        <div className="self-center mb-2">
          <Supporter />
        </div>
      </ScrollableContent>

      <FormFooter
        ready={isValid}
        loading={loading}
        error={error}
        onBack={handleBack}
        onSubmit={handleSubmit}
      />
    </PageLayout>
  );
}

export default Name;
